import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { SegmentController } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import { getFeatureValue } from '../../../selectors/featureSelectors';
import { DMB_FHA_TIMELAPSE, MICROSOFT_WEATHER_GDD, MICROSOFT_WEATHER_GDD_GLOBAL_RELEASE, PLANET_LABS_POC } from '../../../constants/featureFlags';
import styles from './FHAPanel.css';
import { trackHeapEvent } from '../../../actions/analyticsActions';

import { FHAEvent } from '../../../components/cards/FHAEvent/FHAEvent';

import {
  activeSubLayerSelector,
  currentSeasonFHAEventsSelector,
  hasHistoricWeatherSelector,
} from '../../../selectors/fhaSelectors';
import { getFhaTimelapsePlaying } from '../../../selectors/fhaTimelapseSelectors';
import { setActiveSubLayer } from '../../../actions/layersActions';
import { reqObservations } from '../../../actions/weatherActions';

import { FHA, FHA_KEY } from '../../../constants/mapLayerIds';
import { getCurrentSeason } from '../../../selectors/seasonSelectors';

export class FHAEvents extends Component {
  UNSAFE_componentWillMount() {
    const {
      reqObservations: reqObservationsProp,
      season,
      fieldId,
      useMicrosoftGDD
    } = this.props;
    reqObservationsProp(fieldId, season, useMicrosoftGDD);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { reqObservations: reqObservationsProp, season, useMicrosoftGDD } = this.props;
    const nextYear = nextProps.season.split(":")[0]
    const currentYear = season.split(":")[0]
    if (nextYear !== currentYear) {
      reqObservationsProp(nextProps.fieldId, nextProps.season, useMicrosoftGDD);
    }
  }

  componentDidUpdate(prevProps) {
    if (!this.props.isFhaTimelapseEnabled) return;

    const wasPlaying = prevProps.isFhaTimelapsePlaying;
    const isPlaying = this.props.isFhaTimelapsePlaying;
    const prevDate = prevProps.activeEvent?.date;
    const currentDate = this.props.activeEvent?.date;

    const justPaused = wasPlaying && !isPlaying;
    const dateChangedWhilePaused = !isPlaying && prevDate !== currentDate;

    if (justPaused || dateChangedWhilePaused) {
      this.scrollActiveCardIntoView();
    }
  }

  setEventsRef = (el) => {
    this.eventsRef = el;
  };

  scrollActiveCardIntoView = () => {
    const activeDate = this.props.activeEvent?.date;
    if (!activeDate || !this.eventsRef) return;
    const card = this.eventsRef.querySelector(`[id="${activeDate}"]`);
    if (card?.scrollIntoView) {
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  handleTabClick = (tab) => {
    const {
      index,
      season,
      activeEvent,
      setActiveSubLayer: setActiveSubLayerProp,
    } = this.props;
    const activeSubLayer = {
      layer: tab.id,
      date: activeEvent.date,
      segmentSelected: tab.id,
    };
    setActiveSubLayerProp(FHA_KEY, activeSubLayer, index, season);
  };

  handleCardClick = (e, provider) => {
    const {
      index,
      season,
      segmentSelected,
      collapsePanel,
      setActiveSubLayer: setActiveSubLayerProp,
      trackHeapEvent: trackHeapEventProp,
      fieldId,
    } = this.props;
    const activeSubLayer = {
      layer: segmentSelected,
      date: e.currentTarget.id,
      segmentSelected,
    };
    setActiveSubLayerProp(FHA_KEY, activeSubLayer, index, season);

    trackHeapEventProp('View FHA Image Provider', {
      provider: provider || 'unknown',
      fhaType: segmentSelected,
      fieldId,
      imageDate: e.currentTarget.id,
    });

    collapsePanel();
  };

  render() {
    const {
      events,
      hasHistoricWeather,
      usePlanetLabsImagery,
      microsoftGDDGlobalRelease,
      plantingDate,
      activeEvent,
      locale,
      segmentSelected: fhaSelected,
    } = this.props;

    const tabs = [
      { id: FHA.SCOUTING, value: getMessage('fha.scouting') },
      { id: FHA.VEGETATION, value: getMessage('fha.vegetation') },
    ];

    return (
      <div className={styles.panel}>
        <div className={styles.segment}>
          <SegmentController
            className={styles.tabs}
            handleClick={this.handleTabClick}
            selected={fhaSelected}
            tabs={tabs}
          />
        </div>
        <div className={styles.events} ref={this.setEventsRef}>
          {events.map((event, eventIndex) => {
            const provider = event[fhaSelected]?.provider;
            return (
              <FHAEvent
                key={event.date}
                id={event.date}
                date={event.date}
                eventIndex={eventIndex}
                gdus={event.gdus}
                hasHistoricWeather={hasHistoricWeather}
                imageUrl={event[fhaSelected].thumbnailUri}
                isActive={activeEvent.date === event.date}
                locale={locale}
                lowPercent={event.lowPercent}
                onClick={(e) => this.handleCardClick(e, provider)}
                plantingDate={plantingDate}
                precipitation={event.precipitation}
                provider={provider}
                showImageryProvider={usePlanetLabsImagery}
                microsoftGDDGlobalRelease={microsoftGDDGlobalRelease}
              />
            );
          })}
        </div>
      </div>
    );
  }
}

FHAEvents.propTypes = {
  activeEvent: PropTypes.object.isRequired,
  collapsePanel: PropTypes.func,
  events: PropTypes.array.isRequired,
  fieldId: PropTypes.string,
  hasHistoricWeather: PropTypes.bool.isRequired,
  isFhaTimelapseEnabled: PropTypes.bool,
  isFhaTimelapsePlaying: PropTypes.bool,
  usePlanetLabsImagery: PropTypes.bool.isRequired, 
  index: PropTypes.number.isRequired,
  locale: PropTypes.string,
  plantingDate: PropTypes.string,
  reqObservations: PropTypes.func,
  season: PropTypes.string.isRequired,
  segmentSelected: PropTypes.string,
  setActiveSubLayer: PropTypes.func.isRequired,
  trackHeapEvent: PropTypes.func.isRequired
};

function mapStateToProps(state, props) {
  const season = getCurrentSeason(state, props);
  const activeEvent = activeSubLayerSelector(state, props);
  const { segmentSelected } = activeEvent;
  const plantingData = state.planting[season];

  return {
    fieldId: state.field.id,
    locale: state.app.locale,
    events: currentSeasonFHAEventsSelector(state, props),
    hasHistoricWeather: hasHistoricWeatherSelector(state),
    activeEvent: activeSubLayerSelector(state, props),
    isFhaTimelapseEnabled: getFeatureValue(state, { key: DMB_FHA_TIMELAPSE }),
    isFhaTimelapsePlaying: getFhaTimelapsePlaying(state),
    plantingDate: plantingData && plantingData.date,
    useMicrosoftGDD: getFeatureValue(state, { key: MICROSOFT_WEATHER_GDD }),
    usePlanetLabsImagery: getFeatureValue(state, { key: PLANET_LABS_POC }),
    microsoftGDDGlobalRelease: getFeatureValue(state, { key: MICROSOFT_WEATHER_GDD_GLOBAL_RELEASE }),
    segmentSelected,
    season,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    ...bindActionCreators(
      {
        reqObservations,
        setActiveSubLayer,
        trackHeapEvent,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(FHAEvents);
